import { useEffect } from 'react';
import { ObjectType } from '@fullstory/proto/fs/services/dx-objects';
import { OptionType } from '@fullstory/components';
import { SpaceActionType } from '../../types';
import { useFlags } from '@fullstory/react-hooks';
import { useActiveSpaceContext } from '../../contexts';
import { typeSupportsFeatureCard } from '../../utils';
import { useDefaultSpace } from './useDefaultSpace';

/**
 * Returns the space owner for the given object type and id.
 * @param objectId object id to search for
 * @param objectType the ObjectType to search for
 * @param enabled if the feature is enabled or not
 * @param shouldFetch whether the hook should dispatch an explicit ownership request
 * @returns Returns the owning space for an object. If enabled is false or if the
 *          object isn't found in our ownership reducer, this returns undefined.
 */
export const useDefaultSpaceSelectOption = (
  open: boolean,
  objectType: ObjectType,
  objectOwningSpaceId: string,
  selectedSpace: OptionType<string> | null,
  spaceOptions: OptionType<string>[],
  onSpaceSelect: (option: OptionType<string> | null) => void,
  mode: SpaceActionType,
) => {
  const flags = useFlags();
  const defaultSpace = useDefaultSpace();
  const { activeSpace } = useActiveSpaceContext();

  const featuringEnabled = typeSupportsFeatureCard(objectType, flags);
  const disableOwningSpaceOption = mode === 'transfer' || (mode === 'add' && !featuringEnabled);

  useEffect(() => {
    const activeOption = spaceOptions.find(o => o.value === activeSpace?.space?.id);
    const owningOption = spaceOptions.find(o => o.value === objectOwningSpaceId);
    const uniqueActiveOption = activeOption && activeOption?.value !== owningOption?.value;
    const shouldDefaultOption = !disableOwningSpaceOption || uniqueActiveOption;
    if (open && !selectedSpace && shouldDefaultOption) {
      const defaultSpaceId = defaultSpace?.id;
      const defaultSpaceOption = spaceOptions.find(o => o.value === defaultSpaceId);
      const defaultOption = activeOption ?? owningOption ?? defaultSpaceOption;
      if (defaultOption) onSpaceSelect(defaultOption);
    }
  }, [
    activeSpace?.space?.id,
    disableOwningSpaceOption,
    spaceOptions,
    open,
    selectedSpace,
    onSpaceSelect,
    objectOwningSpaceId,
    defaultSpace?.id,
  ]);
};
